/**
 * Date bucketing for matrix row/column groups (display labels + stable bucket keys).
 */

export const DATE_GROUP_GRANULARITIES = [
  { value: 'month', label: 'Month & Year' },
  { value: 'quarter', label: 'Quarter & Year' },
  { value: 'year', label: 'Year' },
  { value: 'day', label: 'Day' },
];

export const DEFAULT_DATE_GROUP_GRANULARITY = 'month';

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function isDateGroupField(field) {
  const dt = String(field?.data_type || field?.field_type || '').toLowerCase();
  return dt === 'date' || dt === 'datetime';
}

export function normalizeDateGroupGranularity(value) {
  const v = String(value || DEFAULT_DATE_GROUP_GRANULARITY).toLowerCase();
  if (DATE_GROUP_GRANULARITIES.some(g => g.value === v)) return v;
  return DEFAULT_DATE_GROUP_GRANULARITY;
}

/** Resolve granularity for a group field (per-field override or matrix default). */
export function resolveDateGranularityForField(field, dateGranularityByFieldId, matrixDefault) {
  const fieldId = field?.id || field?.field_config_id;
  if (fieldId && dateGranularityByFieldId && dateGranularityByFieldId[fieldId]) {
    return normalizeDateGroupGranularity(dateGranularityByFieldId[fieldId]);
  }
  if (field?.dateGroupGranularity) {
    return normalizeDateGroupGranularity(field.dateGroupGranularity);
  }
  return normalizeDateGroupGranularity(matrixDefault);
}

export function parseDateFromFieldValue(raw) {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;

  if (typeof raw === 'object' && raw !== null) {
    const inner = raw.value ?? raw.displayValue;
    return parseDateFromFieldValue(inner);
  }

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t || t === 'N/A') return null;
    // ISO date / datetime
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
      const d = new Date(t);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  return null;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

/**
 * @returns {{ key: string, label: string }}
 */
export function bucketDateForMatrix(date, granularity) {
  const g = normalizeDateGroupGranularity(granularity);
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();

  switch (g) {
    case 'year':
      return { key: `${y}`, label: `${y}` };
    case 'quarter': {
      const q = Math.floor((m - 1) / 3) + 1;
      return { key: `${y}-Q${q}`, label: `Q${q} ${y}` };
    }
    case 'day':
      return {
        key: `${y}-${pad2(m)}-${pad2(d)}`,
        label: `${d} ${MONTH_SHORT[m - 1]} ${y}`,
      };
    case 'month':
    default:
      return {
        key: `${y}-${pad2(m)}`,
        label: `${MONTH_SHORT[m - 1]} ${y}`,
      };
  }
}

export function getRawFieldValueFromRow(row, field) {
  const tableName = field.object_name || 'main';
  const aliased = `${tableName}_${field.field_name}`;
  return row[aliased] ?? row[field.field_name];
}

function isPicklistDataType(field) {
  const dt = String(field?.data_type || field?.field_type || '').toLowerCase();
  return dt === 'picklist' || dt === 'multipicklist';
}

function normalizePicklistValuesOnField(field) {
  const raw = field?.picklist_values_data || field?.picklist_values || field?.picklistValues;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Map stored picklist key → display label using field metadata.
 */
export function resolvePicklistDisplayForFieldValue(fieldData, field) {
  let rawKey = '';
  let display = '';

  if (fieldData != null && typeof fieldData === 'object') {
    rawKey =
      fieldData.value != null && fieldData.value !== ''
        ? String(fieldData.value)
        : '';
    display =
      fieldData.displayValue != null && fieldData.displayValue !== ''
        ? String(fieldData.displayValue)
        : rawKey;
  } else if (fieldData != null && fieldData !== undefined && fieldData !== '') {
    rawKey = String(fieldData);
    display = rawKey;
  }

  if (!isPicklistDataType(field)) {
    return { key: display || 'N/A', label: display || 'N/A', rawKey };
  }

  const picklistValues = normalizePicklistValuesOnField(field);
  if (!picklistValues.length) {
    return { key: display || 'N/A', label: display || 'N/A', rawKey };
  }

  const matchToken = (token) => {
    const t = String(token ?? '').trim().toLowerCase();
    if (!t) return null;
    return picklistValues.find((pv) => {
      const candidates = [
        pv.value_key,
        pv.key,
        pv.value_label,
        pv.label,
        pv.value,
      ]
        .filter((v) => v != null && String(v).trim() !== '')
        .map((v) => String(v).trim().toLowerCase());
      return candidates.includes(t);
    });
  };

  const match =
    matchToken(display) ||
    matchToken(rawKey) ||
    (rawKey ? matchToken(rawKey) : null);

  if (match) {
    const label =
      match.value_label || match.label || match.value || display || rawKey;
    return {
      key: String(label),
      label: String(label),
      rawKey: match.value_key || match.key || rawKey,
    };
  }

  return { key: display || 'N/A', label: display || 'N/A', rawKey };
}

/**
 * Group bucket for one field on one row (dates → month/quarter/year; others → string).
 */
export function getGroupBucketFromRow(row, field, options = {}) {
  const { dateGranularityByFieldId = {}, matrixDateGranularity = DEFAULT_DATE_GROUP_GRANULARITY } =
    options;

  const fieldData = getRawFieldValueFromRow(row, field);

  if (!isDateGroupField(field)) {
    if (isPicklistDataType(field)) {
      const resolved = resolvePicklistDisplayForFieldValue(fieldData, field);
      return { key: resolved.key, label: resolved.label };
    }
    let text = 'N/A';
    if (fieldData != null && typeof fieldData === 'object') {
      const v = fieldData.displayValue ?? fieldData.value;
      if (v !== undefined && v !== null && v !== '') text = String(v);
    } else if (fieldData !== null && fieldData !== undefined && fieldData !== '') {
      text = String(fieldData);
    }
    return { key: text, label: text };
  }

  const granularity = resolveDateGranularityForField(
    field,
    dateGranularityByFieldId,
    matrixDateGranularity
  );
  const parsed = parseDateFromFieldValue(fieldData);
  if (!parsed) {
    return { key: 'N/A', label: 'N/A' };
  }
  return bucketDateForMatrix(parsed, granularity);
}

/** Apply matrix date granularity onto group field copies for pivot. */
export function applyDateGranularityToGroups(groups, dateGranularityByFieldId, matrixDateGranularity) {
  return (groups || []).map(g => ({
    ...g,
    dateGroupGranularity: resolveDateGranularityForField(
      g,
      dateGranularityByFieldId,
      matrixDateGranularity
    ),
  }));
}

export function matrixConfigHasDateGroups(rowGroups, columnGroups) {
  return [...(rowGroups || []), ...(columnGroups || [])].some(isDateGroupField);
}

export function buildDateGroupOptions(
  matrixDateGranularity = DEFAULT_DATE_GROUP_GRANULARITY,
  dateGranularityByFieldId = {}
) {
  return {
    matrixDateGranularity: normalizeDateGroupGranularity(matrixDateGranularity),
    dateGranularityByFieldId: dateGranularityByFieldId || {},
  };
}

/**
 * Numeric sort key for group bucket labels/keys (month, quarter, year, ISO).
 * Used for MoM growth and chronological group row order.
 */
export function parseReportGroupBucketSortTime(key) {
  const s = String(key ?? '').trim();
  if (!s || s === 'N/A') return null;

  const isoMonth = /^(\d{4})-(\d{2})$/.exec(s);
  if (isoMonth) {
    return new Date(
      parseInt(isoMonth[1], 10),
      parseInt(isoMonth[2], 10) - 1,
      1
    ).getTime();
  }

  const monYear = /^([A-Za-z]{3})\s+(\d{4})$/.exec(s);
  if (monYear) {
    const mi = MONTH_SHORT.findIndex(
      (m) => m.toLowerCase() === monYear[1].toLowerCase().slice(0, 3)
    );
    if (mi >= 0) {
      return new Date(parseInt(monYear[2], 10), mi, 1).getTime();
    }
  }

  const dayLabel = /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/.exec(s);
  if (dayLabel) {
    const mi = MONTH_SHORT.findIndex(
      (m) => m.toLowerCase() === dayLabel[2].toLowerCase().slice(0, 3)
    );
    if (mi >= 0) {
      return new Date(
        parseInt(dayLabel[3], 10),
        mi,
        parseInt(dayLabel[1], 10)
      ).getTime();
    }
  }

  const quarterLabel = /^Q([1-4])\s+(\d{4})$/i.exec(s);
  if (quarterLabel) {
    const q = parseInt(quarterLabel[1], 10);
    return new Date(parseInt(quarterLabel[2], 10), (q - 1) * 3, 1).getTime();
  }

  const quarterKey = /^(\d{4})-Q([1-4])$/i.exec(s);
  if (quarterKey) {
    const q = parseInt(quarterKey[2], 10);
    return new Date(parseInt(quarterKey[1], 10), (q - 1) * 3, 1).getTime();
  }

  const parsed = parseDateFromFieldValue(s);
  return parsed ? parsed.getTime() : null;
}

export function compareReportGroupBucketKeys(a, b) {
  const ta = parseReportGroupBucketSortTime(a);
  const tb = parseReportGroupBucketSortTime(b);
  if (ta != null && tb != null) return ta - tb;
  if (ta != null) return -1;
  if (tb != null) return 1;
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/** Stable key + display label for a grouped field on one data row. */
export function getReportGroupBucket(field, row, dateOptions = {}) {
  return getGroupBucketFromRow(row, field, dateOptions);
}

export function parseMatrixDateConfig(reportConfig) {
  const cfg =
    reportConfig == null
      ? {}
      : typeof reportConfig === 'string'
        ? (() => {
            try {
              return JSON.parse(reportConfig);
            } catch {
              return {};
            }
          })()
        : reportConfig;
  const matrix = cfg.matrix || {};
  return {
    matrixDateGranularity: normalizeDateGroupGranularity(
      matrix.dateGranularity || DEFAULT_DATE_GROUP_GRANULARITY
    ),
    dateGranularityByFieldId:
      matrix.dateGranularityByFieldId && typeof matrix.dateGranularityByFieldId === 'object'
        ? matrix.dateGranularityByFieldId
        : {},
  };
}

export function buildMatrixConfigPatch(existingConfig, { matrixDateGranularity, dateGranularityByFieldId }) {
  const base =
    existingConfig == null
      ? {}
      : typeof existingConfig === 'string'
        ? (() => {
            try {
              return JSON.parse(existingConfig);
            } catch {
              return {};
            }
          })()
        : { ...existingConfig };
  return {
    ...base,
    matrix: {
      ...(base.matrix || {}),
      dateGranularity: normalizeDateGroupGranularity(matrixDateGranularity),
      dateGranularityByFieldId: dateGranularityByFieldId || base.matrix?.dateGranularityByFieldId || {},
    },
  };
}
